import React, { useState, useEffect, useRef } from 'react';
import { claimAudioChannel, releaseAudioChannel } from '../utils/audioChannel';

// Get diplomacy video for country pair (randomly selects if multiple exist)
const getDiplomacyVideo = (sourceCode, targetCode) => {
  // Normalize pair (alphabetical order for consistent lookup)
  const pair = [sourceCode, targetCode].sort().join('-');
  
  // Map of available videos (arrays support multiple variants)
  const videos = {
    'CHN-NK': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/han84fb7_Diplomacy%20China%20NKorea%20Good.mp4'],
    'CHN-RUS': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ceib07l5_Diplomacy%20China%20Russia.mp4'],
    'CHN-USA': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/kvg4pyiv_Diplomacy%20USA%20China.mp4'],
    'CHN-EU': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/hcmwzokk_Diplomacy%20China%20EU.mp4'], // ✅ COMPLETE!
    'EU-NK': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/7gs5mb96_Diplomacy%20EU%20%26%20NKorea.mp4'],
    'EU-RUS': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/l0dfnd1y_Diplomacy%20EU%20%26%20Russia.mp4'],
    'EU-USA': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/nv9jwlwa_Diplomacy%20USA%20EU.mp4'],
    'NK-RUS': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/6snxauyy_Diplomacy%20NKorea%20%26%20Russia.mp4'],
    'NK-USA': [
      'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/gz3fot2p_Diplomacy%20USA%20NKorea%203.mp4',
      'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/cugafh40_Diplomacy%20USA%20Nkorea.mp4' // Variant 2!
    ],
    'RUS-USA': ['https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/7fvnm42c_Diplomacy%20USA%20RUssia.mp4'],
  };
  
  const available = videos[pair] || [];
  if (available.length === 0) {
    console.warn(`No diplomacy video for pair: ${pair}`);
    return null;
  }
  
  // Randomly select from available variants
  const selected = available[Math.floor(Math.random() * available.length)];
  console.log(`✅ Selected diplomacy video for ${pair}:`, selected);
  return selected;
};

// Camera Flash Component
function CameraFlash({ position, delay }) {
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
      setTimeout(() => setVisible(false), 150); // Flash duration
    }, delay);
    
    return () => clearTimeout(timer);
  }, [delay]);
  
  if (!visible) return null;
  
  return (
    <div
      className="absolute w-24 h-24 bg-white rounded-full opacity-80 animate-ping"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        animationDuration: '0.15s',
        animationIterationCount: 1,
      }}
    />
  );
}

export default function DiplomacyAnimation({ sourceCode, targetCode, onComplete }) {
  const [flashes, setFlashes] = useState([]);
  const video = getDiplomacyVideo(sourceCode, targetCode);
  const audioRefs = useRef([]);
  const videoRef = useRef(null);
  
  useEffect(() => {
    // 🔈 Claim the audio channel so layered news intro/applause SFX play
    claimAudioChannel();

    // Auto-play video if available
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log('Video autoplay blocked:', e));
    }
    
    // Generate random camera flashes
    const flashPositions = [];
    for (let i = 0; i < 12; i++) {
      flashPositions.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 4500,
      });
    }
    setFlashes(flashPositions);
    
    // === LAYERED AUDIO SYSTEM ===
    audioRefs.current = []; // Initialize empty array
    
    try {
      // 1. News Intro - Dramatic opening (plays immediately)
      const newsIntro = new Audio('/assets/sounds/news-intro.mp3');
      newsIntro.volume = 0.6 + Math.random() * 0.2; // 0.6-0.8
      newsIntro.play().catch(e => console.log('News intro autoplay blocked:', e));
      audioRefs.current.push(newsIntro);
      
      // 2. Applause - Crowd cheering (starts after 0.5s, loops)
      const applause = new Audio('/assets/sounds/applause.mp3');
      applause.volume = 0.4 + Math.random() * 0.2; // 0.4-0.6
      applause.loop = true;
      audioRefs.current.push(applause); // Add to cleanup array immediately
      setTimeout(() => {
        applause.play().catch(e => console.log('Applause autoplay blocked:', e));
      }, 500);
      
      // 3. Camera Shutters - Rapid clicking (random timing, 2-3 times)
      const shutterCount = 2 + Math.floor(Math.random() * 2); // 2-3 times
      for (let i = 0; i < shutterCount; i++) {
        setTimeout(() => {
          const shutter = new Audio('/assets/sounds/camera-shutter.mp3');
          shutter.volume = 0.5 + Math.random() * 0.2; // 0.5-0.7
          shutter.play().catch(e => console.log('Shutter autoplay blocked:', e));
          audioRefs.current.push(shutter);
        }, 1000 + (i * 1500) + Math.random() * 500); // Staggered timing
      }
      
      // 4. Camera Flashes - Single pops (random throughout, 4-6 times)
      const flashCount = 4 + Math.floor(Math.random() * 3); // 4-6 times
      for (let i = 0; i < flashCount; i++) {
        setTimeout(() => {
          const flash = new Audio('/assets/sounds/camera-flash.mp3');
          flash.volume = 0.6 + Math.random() * 0.2; // 0.6-0.8
          flash.play().catch(e => console.log('Flash autoplay blocked:', e));
          audioRefs.current.push(flash);
        }, Math.random() * 4000); // Random throughout 4 seconds
      }
    } catch (error) {
      console.error('Audio playback error:', error);
    }
    
    // Complete animation after 5 seconds
    const timer = setTimeout(() => {
      // Stop all audio
      console.log('Stopping all diplomacy audio...', audioRefs.current.length, 'tracks');
      audioRefs.current.forEach(audio => {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.loop = false; // Ensure loops are stopped
        } catch (e) {
          console.log('Audio cleanup error:', e);
        }
      });
      audioRefs.current = []; // Clear array
      
      if (onComplete) onComplete();
    }, 5000);
    
    return () => {
      clearTimeout(timer);
      // Cleanup audio on unmount
      console.log('Unmount cleanup:', audioRefs.current.length, 'tracks');
      audioRefs.current.forEach(audio => {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.loop = false;
        } catch (e) {}
      });
      audioRefs.current = [];
      // 🔈 Release audio channel so background music resumes
      releaseAudioChannel();
    };
  }, [onComplete]);
  
  if (!video) {
    if (onComplete) onComplete();
    return null;
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 animate-fadeIn">
      {/* Yellow Spotlight Effect */}
      <div 
        className="absolute inset-0 diplomacy-spotlight"
        style={{
          background: 'radial-gradient(circle at center, rgba(255, 215, 0, 0.4) 0%, rgba(255, 215, 0, 0.2) 30%, transparent 60%)',
        }}
      />
      
      {/* Camera Flashes */}
      {flashes.map(flash => (
        <CameraFlash key={flash.id} position={{ x: flash.x, y: flash.y }} delay={flash.delay} />
      ))}
      
      {/* Diplomacy Video */}
      <div className="relative z-10 animate-fadeIn">
        <video
          ref={videoRef}
          src={video}
          autoPlay
          muted
          playsInline
          className="max-w-[80vw] max-h-[80vh] object-contain drop-shadow-2xl"
          style={{
            filter: 'drop-shadow(0 0 40px rgba(255, 215, 0, 0.6))',
          }}
        />
      </div>
      
      {/* Diplomatic Text */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-center z-10">
        <div className="text-yellow-400 text-4xl font-bold uppercase tracking-wider animate-pulse mb-2">
          🤝 DIPLOMATIC SUMMIT 🤝
        </div>
        <div className="text-yellow-300 text-xl">
          Press Conference in Progress
        </div>
      </div>
    </div>
  );
}
